import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { FirebaseAuthService } from '../services/firebaseAuthService';
import { AppRole, AppPermission } from '../types';
import { ROLE_PERMISSIONS } from '../services/permissionsService';
import { userManagementService } from '../services/userManagementService';
import { inactiveUserService } from '../services/inactiveUserService';
import { auth } from '../lib/firebase';

interface PermissionsContextType {
  role: AppRole;
  setRole: (role: AppRole) => void;
  permissions: AppPermission[];
  isLoading: boolean;
  hasPermission: (permission: AppPermission) => boolean;
  hasRole: (role: AppRole) => boolean;
  hasAnyPermission: (permissions: AppPermission[]) => boolean;
  hasAllPermissions: (permissions: AppPermission[]) => boolean;
  reloadPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const [role, setRoleState] = useState<AppRole>('user');
  const [permissions, setPermissions] = useState<AppPermission[]>(ROLE_PERMISSIONS.user || []);
  const [isLoading, setIsLoading] = useState(true);
  const requestIdRef = React.useRef(0);

  const setRole = (newRole: AppRole) => {
    setRoleState(newRole);
    setPermissions(ROLE_PERMISSIONS[newRole] || []);
  };

  const loadUserPermissions = async () => {
    const currentReqId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const firebaseUser = auth.currentUser;
      
      if (firebaseUser) {
        const rawEmail = firebaseUser.email || '';
        const email = rawEmail.trim().toLowerCase();

        // 1. Carrega dados do Firestore
        let firestoreProfile = await FirebaseAuthService.getUserProfile(firebaseUser.uid);

        // Se for o administrador master, garante a role de admin no Firestore e em memória!
        if (email === 'hugo.abdalaengenharia@gmail.com') {
          if (!firestoreProfile || firestoreProfile.role !== 'admin' || !firestoreProfile.active) {
            console.log('Bootstrapping admin profile for master user:', email);
            await FirebaseAuthService.saveUserProfile(firebaseUser.uid, {
              id: 9001,
              email: email,
              displayName: firebaseUser.displayName || 'Hugo Abdala (Administrador)',
              role: 'admin',
              active: true,
              groupIds: [1]
            });
            firestoreProfile = await FirebaseAuthService.getUserProfile(firebaseUser.uid);
          }
        }

        if (currentReqId !== requestIdRef.current) return;

        // Se inativo, bloqueia
        if (firestoreProfile && firestoreProfile.active === false) {
          console.warn(`Permissões negadas: Usuário ${email} está inativo no Firebase.`);
          inactiveUserService.setInactiveNotice(email, firestoreProfile.displayName || '');
          await FirebaseAuthService.logout();
          if (currentReqId === requestIdRef.current) {
            setRoleState('guest');
            setPermissions([]);
            setIsLoading(false);
          }
          return;
        }

        // 1. Se o perfil do Firestore tem permissões customizadas salvas, utiliza-as prioritariamente
        if (firestoreProfile?.custom_permissions && Array.isArray(firestoreProfile.custom_permissions)) {
          if (currentReqId === requestIdRef.current) {
            setRoleState(firestoreProfile.role || 'user');
            setPermissions(firestoreProfile.custom_permissions);
            setIsLoading(false);
          }
          return;
        }

        // 2. Tenta sincronizar com cadastro de usuários da transportadora
        try {
          const usersWithPerms = await userManagementService.getUsuariosComPermissoes();
          if (currentReqId !== requestIdRef.current) return;

          const currentProfile = usersWithPerms.find(u =>
            (u.email && u.email.trim().toLowerCase() === email) || u.auth_user_id === firebaseUser.uid
          );

          if (currentProfile) {
            if (currentReqId === requestIdRef.current) {
              setRoleState(currentProfile.role || 'user');
              if (currentProfile.custom_permissions && Array.isArray(currentProfile.custom_permissions)) {
                setPermissions(currentProfile.custom_permissions);
              } else {
                setPermissions(ROLE_PERMISSIONS[currentProfile.role] || ROLE_PERMISSIONS.user);
              }
              setIsLoading(false);
            }
            return;
          }
        } catch (e) {
          console.warn('Erro ao consultar permissões customizadas de usuários:', e);
        }

        if (currentReqId !== requestIdRef.current) return;

        // 3. Se tem role no Firestore sem permissões customizadas
        if (firestoreProfile?.role) {
          if (currentReqId === requestIdRef.current) {
            setRoleState(firestoreProfile.role);
            setPermissions(ROLE_PERMISSIONS[firestoreProfile.role] || ROLE_PERMISSIONS.user);
            setIsLoading(false);
          }
          return;
        }

        // 4. Sem cadastro configurado (nem system_users, nem role no Firestore):
        // fail-closed de verdade. Um e-mail é auto-declarado pelo próprio usuário
        // no Firebase, então inferir o papel por palavras como "operacional" ou
        // "comercial" contidas no e-mail permitia que qualquer pessoa escolhesse
        // o próprio nível de permissão (incluía até manage_vehicles/manage_orders).
        // Sem registro explícito = guest, sem nenhuma permissão, até um
        // administrador atribuir o papel correto pela tela de usuários.
        if (currentReqId === requestIdRef.current) {
          setRoleState('guest');
          setPermissions([]);
        }
      } else {
        if (currentReqId === requestIdRef.current) {
          setRoleState('guest');
          setPermissions([]);
        }
      }
    } catch (err) {
      console.warn('Erro ao carregar permissões do Firebase:', err);
      if (currentReqId === requestIdRef.current) {
        // Fail closed em erro: atribui perfil restrito de usuário padrão, nunca admin
        setRoleState('user');
        setPermissions(ROLE_PERMISSIONS.user);
      }
    } finally {
      if (currentReqId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadUserPermissions();

    const unsubscribe = FirebaseAuthService.onAuthStateChange((user) => {
      if (user) {
        loadUserPermissions();
      } else {
        setRoleState('guest');
        setPermissions([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const hasPermission = (permission: AppPermission) => {
    if (role === 'admin') return true;
    if (permissions && Array.isArray(permissions)) {
      if (permissions.includes(permission)) return true;
      if (permission === 'view_fiscal' && (permissions.includes('view_financial') || permissions.includes('view_orders') || permissions.includes('view_dashboard'))) return true;
      if (permission === 'manage_fiscal' && (permissions.includes('manage_financial') || permissions.includes('manage_orders'))) return true;
      if (permission === 'emit_cte' && (permissions.includes('view_orders') || permissions.includes('manage_orders'))) return true;
      if (permission === 'emit_nfse' && (permissions.includes('view_financial') || permissions.includes('manage_medicoes'))) return true;
    }
    return false;
  };

  const hasRole = (r: AppRole) => {
    if (role === 'admin') return true;
    return role === r;
  };

  const hasAnyPermission = (perms: AppPermission[]) => {
    if (role === 'admin') return true;
    if (permissions && Array.isArray(permissions)) {
      return perms.some((p) => hasPermission(p));
    }
    return false;
  };

  const hasAllPermissions = (perms: AppPermission[]) => {
    if (role === 'admin') return true;
    if (permissions && Array.isArray(permissions)) {
      return perms.every((p) => hasPermission(p));
    }
    return false;
  };

  return (
    <PermissionsContext.Provider value={{ role, setRole, permissions, isLoading, hasPermission, hasRole, hasAnyPermission, hasAllPermissions, reloadPermissions: loadUserPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissionsContext() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissionsContext must be used within a PermissionsProvider');
  }
  return context;
}
